import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authAPI } from "@/services/authAPI";
import { tokenService } from "@/services/tokenService";
import type { LoginCredentials, AdminUser } from "@/types";

// Query keys
export const authKeys = {
  all: ["auth"] as const,
  verify: () => [...authKeys.all, "verify"] as const,
};

// Hook for login mutation
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => authAPI.login(credentials),
    onSuccess: (response) => {
      // Store access token from data.tokens
      tokenService.set(response.data.tokens.accessToken);

      // Update the verify query cache with admin user data
      queryClient.setQueryData(authKeys.verify(), response.data.admin);
    },
    onError: (error: Error) => {
      console.error("Login failed:", error.message);
      tokenService.clear();
    },
  });
};

// Hook for verify token query
export const useVerifyToken = (enabled: boolean = true) => {
  return useQuery({
    queryKey: authKeys.verify(),
    queryFn: authAPI.verifyToken,
    enabled: enabled && !!tokenService.get(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook for logout mutation
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authAPI.logout,
    onSettled: () => {
      queryClient.clear();
    },
  });
};

// Hook to get current user from cache
export const useCurrentUser = (): AdminUser | undefined => {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<AdminUser>(authKeys.verify());
};
